import { Suspense, lazy } from 'react';
import { Dialog, Typography } from '@mui/material';
import { useSharedPdfViewerGate } from '@liscio/common';
import { PdfDocumentViewer } from '@liscio/ui';
import { Header } from 'ui/Header/Header';
import PDFViewerErrorBoundary from 'common/PDFViewerErrorBoundary/PDFViewerErrorBoundary';
import { reportPdfViewerError } from 'common/PDFViewerErrorBoundary/reportPdfViewerError';

type Props = {
  url: string;
  fileName: string;
  open: boolean;
  headerTitle: string;
  onClose: () => void;
};

const PSPDFViewer = lazy(() => import('common/PSPDFKit/PSPDFViewer'));

const pdfViewerFallbackStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
};

const pdfViewerFallback = (
  <div
    role="status"
    aria-label="Loading PDF viewer"
    style={pdfViewerFallbackStyle}
  >
    Loading PDF viewer...
  </div>
);

export default function FileDisplayFromUrl({
  url,
  open,
  headerTitle,
  fileName,
  onClose,
}: Props) {
  const isPdf = url.split('?')[0]?.endsWith('.pdf');
  const sharedViewer = useSharedPdfViewerGate(open ? 'open' : null);

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{ sx: { width: 'calc(100vw - 200px)' } }}
      sx={{ zIndex: 1049 }}
      maxWidth="xl"
    >
      <Header onClose={onClose} title={headerTitle} />
      {open && url && isPdf && sharedViewer && (
        // The shared viewer needs a definite, positioned parent. PSPDFViewer
        // reads `height: inherit` instead, so it doesn't get this wrapper.
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <PDFViewerErrorBoundary key={url} operation="loadPdfDocumentViewer">
            <PdfDocumentViewer
              documentId={url}
              url={url}
              title={fileName}
              onError={reportPdfViewerError}
            />
          </PDFViewerErrorBoundary>
        </div>
      )}
      {open && url && isPdf && !sharedViewer && (
        <PDFViewerErrorBoundary key={url}>
          <Suspense fallback={pdfViewerFallback}>
            <PSPDFViewer url={url} fileName={fileName} />
          </Suspense>
        </PDFViewerErrorBoundary>
      )}
      {url && !isPdf && (
        <iframe title="preview" src={url} width="100%" height="1000px" />
      )}
      {!url && (
        <Typography
          variant="h1"
          color="GrayText"
          display="flex"
          paddingTop="2rem"
          justifyContent="center"
        >
          Preview unavailable
        </Typography>
      )}
    </Dialog>
  );
}
